import type { FC } from 'react';
import React, { useCallback, useState } from 'react';

import { RangeSlider } from 'Components/shared/RangeSlider';

import { DropdownPanel } from '../DropdownPanel';
import type { RangeUnit } from '../FilterBarV2.helpers';
import { formatRangeValue } from '../FilterBarV2.helpers';

import styles from './RangeFilterDropdown.module.scss';

interface RangeFilterDropdownProps {
	disabledFrom?: boolean;
	increment?: number;
	label: string;
	max: number;
	min: number;
	onApply: (from: Nullable<number>, to: Nullable<number>) => void;
	onClear: () => void;
	onClose?: () => void;
	unit?: RangeUnit;
	valueFrom: Nullable<number>;
	valueTo: Nullable<number>;
}

export const RangeFilterDropdown: FC<RangeFilterDropdownProps> = ({
	disabledFrom,
	increment = 1,
	label,
	max,
	min,
	onApply,
	onClear,
	onClose,
	unit = 'price',
	valueFrom,
	valueTo,
}) => {
	const [localFrom, setLocalFrom] = useState<Nullable<number>>(valueFrom);
	const [localTo, setLocalTo] = useState<Nullable<number>>(valueTo);

	const isClear = localFrom === null && localTo === null;

	const handleSliderChange = useCallback(
		(from: number, to: number) => {
			setLocalFrom(from === min ? null : from);
			setLocalTo(to === max ? null : to);
		},
		[max, min],
	);

	const handleApply = useCallback(() => {
		onApply(localFrom, localTo);
		onClose?.();
	}, [localFrom, localTo, onApply, onClose]);

	const handleClear = useCallback(() => {
		setLocalFrom(null);
		setLocalTo(null);
		onClear();
		onClose?.();
	}, [onClear, onClose]);

	const rangeLabel = formatRangeValue({ from: localFrom, max, min, to: localTo, unit });

	const hasChanged = localFrom !== valueFrom || localTo !== valueTo;
	const hasStoreValue = valueFrom !== null || valueTo !== null;

	return (
		<DropdownPanel
			clearDisabled={isClear && !hasStoreValue}
			hasChanged={hasChanged}
			hasStoreValue={hasStoreValue}
			onApply={handleApply}
			onClear={handleClear}
		>
			<div className={styles.rangeHeader}>
				<span className={styles.rangeLabel}>{label}</span>
				<span className={styles.rangeValue}>{rangeLabel}</span>
			</div>
			<RangeSlider
				className={styles.rangeSlider}
				disableFromThumb={disabledFrom}
				from={localFrom ?? min}
				max={max}
				min={min}
				onChange={handleSliderChange}
				step={increment}
				to={localTo ?? max}
			/>
		</DropdownPanel>
	);
};
